/** Normalized crop rect against natural image size (0–1). */
export type ImageCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Non-destructive color adjustments. Range −1…1 (0 = unchanged). */
export type ImageAdjustments = {
  brightness: number;
  contrast: number;
  saturation: number;
  warmth: number;
};

export type ImageAdjustmentsPatch = {
  brightness?: number;
  contrast?: number;
  saturation?: number;
  warmth?: number;
};

export type ImageObjectFit = "cover" | "contain" | "fill";

export type ImageFilterPresetId =
  | "soft"
  | "vivid"
  | "warm"
  | "cool"
  | "bw";

export function clampImageAdjust(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(-1, Math.min(1, Math.round(value * 100) / 100));
}

export function isImageObjectFit(v: unknown): v is ImageObjectFit {
  return v === "cover" || v === "contain" || v === "fill";
}

export function readImageObjectFit(raw: unknown): ImageObjectFit {
  return isImageObjectFit(raw) ? raw : "fill";
}

/**
 * How a source rect (after crop) maps into the block box.
 * `fill` stretches; `contain` letterboxes; `cover` crops to fill.
 */
export function layoutImageInBox(
  boxW: number,
  boxH: number,
  srcW: number,
  srcH: number,
  objectFit: ImageObjectFit,
): { x: number; y: number; width: number; height: number } {
  if (
    objectFit === "fill" ||
    !(boxW > 0 && boxH > 0 && srcW > 0 && srcH > 0)
  ) {
    return { x: 0, y: 0, width: boxW, height: boxH };
  }
  const boxRatio = boxW / boxH;
  const srcRatio = srcW / srcH;
  if (objectFit === "contain") {
    if (srcRatio > boxRatio) {
      const width = boxW;
      const height = boxW / srcRatio;
      return { x: 0, y: (boxH - height) / 2, width, height };
    }
    const height = boxH;
    const width = boxH * srcRatio;
    return { x: (boxW - width) / 2, y: 0, width, height };
  }
  // cover
  if (srcRatio > boxRatio) {
    const height = boxH;
    const width = boxH * srcRatio;
    return { x: (boxW - width) / 2, y: 0, width, height };
  }
  const width = boxW;
  const height = boxW / srcRatio;
  return { x: 0, y: (boxH - height) / 2, width, height };
}

/** Clamp and ensure a minimum crop size (~5% of source). */
export function normalizeImageCrop(
  crop: ImageCropRect | null,
): ImageCropRect | null {
  if (!crop) return null;
  const min = 0.05;
  let x = Math.max(0, Math.min(1, crop.x));
  let y = Math.max(0, Math.min(1, crop.y));
  let width = Math.max(min, Math.min(1 - x, crop.width));
  let height = Math.max(min, Math.min(1 - y, crop.height));
  if (width >= 0.999 && height >= 0.999 && x <= 0.001 && y <= 0.001) {
    return null;
  }
  return {
    x: Math.round(x * 1000) / 1000,
    y: Math.round(y * 1000) / 1000,
    width: Math.round(width * 1000) / 1000,
    height: Math.round(height * 1000) / 1000,
  };
}

export function readImageCrop(raw: unknown): ImageCropRect | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const x = typeof o.x === "number" ? o.x : NaN;
  const y = typeof o.y === "number" ? o.y : NaN;
  const width = typeof o.width === "number" ? o.width : NaN;
  const height = typeof o.height === "number" ? o.height : NaN;
  if (![x, y, width, height].every(Number.isFinite)) return null;
  return normalizeImageCrop({ x, y, width, height });
}

export function emptyImageAdjustments(): ImageAdjustments {
  return { brightness: 0, contrast: 0, saturation: 0, warmth: 0 };
}

export function readImageAdjustments(raw: unknown): ImageAdjustments {
  if (!raw || typeof raw !== "object") {
    return emptyImageAdjustments();
  }
  const o = raw as Record<string, unknown>;
  return {
    brightness: clampImageAdjust(
      typeof o.brightness === "number" ? o.brightness : 0,
    ),
    contrast: clampImageAdjust(
      typeof o.contrast === "number" ? o.contrast : 0,
    ),
    saturation: clampImageAdjust(
      typeof o.saturation === "number" ? o.saturation : 0,
    ),
    warmth: clampImageAdjust(typeof o.warmth === "number" ? o.warmth : 0),
  };
}

export function mergeImageAdjustments(
  prev: ImageAdjustments,
  patch: ImageAdjustmentsPatch,
): ImageAdjustments {
  return {
    brightness:
      patch.brightness !== undefined
        ? clampImageAdjust(patch.brightness)
        : prev.brightness,
    contrast:
      patch.contrast !== undefined
        ? clampImageAdjust(patch.contrast)
        : prev.contrast,
    saturation:
      patch.saturation !== undefined
        ? clampImageAdjust(patch.saturation)
        : prev.saturation,
    warmth:
      patch.warmth !== undefined ? clampImageAdjust(patch.warmth) : prev.warmth,
  };
}

export function isNeutralImageAdjustments(adj: ImageAdjustments): boolean {
  return (
    adj.brightness === 0 &&
    adj.contrast === 0 &&
    adj.saturation === 0 &&
    adj.warmth === 0
  );
}

/** CSS filter string from −1…1 adjustments. */
export function imageAdjustmentsToCssFilter(adj: ImageAdjustments): string {
  const a = readImageAdjustments(adj);
  const parts: string[] = [];
  const b = 1 + a.brightness;
  const c = 1 + a.contrast;
  const s = 1 + a.saturation;
  if (b !== 1) parts.push(`brightness(${b})`);
  if (c !== 1) parts.push(`contrast(${c})`);
  if (s !== 1) parts.push(`saturate(${s})`);
  if (a.warmth !== 0) {
    const sepia = Math.abs(a.warmth) * 0.35;
    const hue = a.warmth * 28;
    if (sepia > 0) parts.push(`sepia(${sepia})`);
    parts.push(`hue-rotate(${hue}deg)`);
  }
  return parts.length === 0 ? "none" : parts.join(" ");
}

export const IMAGE_FILTER_PRESETS: Record<
  ImageFilterPresetId,
  { label: string; patch: ImageAdjustments }
> = {
  soft: {
    label: "Soft",
    patch: { brightness: 0.08, contrast: -0.12, saturation: -0.08, warmth: 0.1 },
  },
  vivid: {
    label: "Vivid",
    patch: { brightness: 0.04, contrast: 0.18, saturation: 0.28, warmth: 0.05 },
  },
  warm: {
    label: "Warm",
    patch: { brightness: 0.06, contrast: 0.04, saturation: 0.08, warmth: 0.45 },
  },
  cool: {
    label: "Cool",
    patch: { brightness: 0.02, contrast: 0.06, saturation: 0.05, warmth: -0.4 },
  },
  bw: {
    label: "B&W",
    patch: { brightness: 0.02, contrast: 0.1, saturation: -1, warmth: 0 },
  },
};
